import { WordLayoutPart, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

import { repeatOf, coveredBy, occurrences, isCommandText, bodyParagraphs, placeholderText } from './word-placeholder-editor.utils';

// The placeholder editor's page view (staging feedback, JT1-1887). docx-preview draws the agency's
// Word file as it looks; these helpers then number its paragraphs and table rows as the server's
// layout does, so selecting works as in the text view, and draw the pending changes onto it.

const CHIP = 'jt-placeholder';
const normalise = (text: string) => text.replace(/\s+/g, ' ').trim();

const mark = (element: HTMLElement, part: string, index: number) => {
  element.dataset.part = part;
  element.dataset.paragraph = String(index);
};

/**
 * Marks the drawn page with the layout's numbering: body paragraphs get `data-part` and
 * `data-paragraph`, table rows `data-table` and `data-row`. Tables and rows are matched in order;
 * paragraphs by their text, in order, a few ahead at most, so one drawn differently doesn't throw
 * out the rest. Paragraphs it can't match, and those in headers and footers, are numbered -1: they
 * still take document-wide placeholders.
 */
export function mapRenderedDocument(root: HTMLElement, parts: WordLayoutPart[]): void {
  const articles = Array.from(root.querySelectorAll<HTMLElement>('section.docx > article'));
  const blocks = articles.flatMap((article) => Array.from(article.querySelectorAll<HTMLElement>('p, table')).filter((element) => !element.parentElement?.closest('table')));
  const paragraphs = blocks.filter((element) => element.tagName === 'P');
  const tables = blocks.filter((element): element is HTMLTableElement => element.tagName === 'TABLE');

  let nextParagraph = 0;
  let nextTable = 0;
  (parts.find((part) => part.kind === 'body')?.items ?? []).forEach((item) => {
    if (item.type === 'table') {
      const table = tables[nextTable];
      nextTable += 1;
      if (!table) return;
      // The table's own rows and cells, not a nested table's.
      const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr')).filter((tr) => tr.closest('table') === table);
      item.rows.forEach((row, r) => {
        const tr = rows[r];
        if (!tr) return;
        tr.dataset.table = String(item.index);
        tr.dataset.row = String(row.index);
        const cells = Array.from(tr.children).filter((cell) => cell.tagName === 'TD' || cell.tagName === 'TH');
        row.cells.forEach((cell, c) => {
          const drawn = Array.from(cells[c]?.querySelectorAll<HTMLElement>('p') ?? []);
          cell.forEach((paragraph, i) => {
            if (drawn[i]) mark(drawn[i], 'body', paragraph.index);
          });
        });
      });
      return;
    }
    const want = normalise(item.text);
    for (let k = nextParagraph; k < Math.min(nextParagraph + 6, paragraphs.length); k += 1) {
      if (normalise(paragraphs[k].textContent ?? '') === want) {
        mark(paragraphs[k], 'body', item.index);
        nextParagraph = k + 1;
        return;
      }
    }
  });

  articles.forEach((article) => article.querySelectorAll<HTMLElement>('p').forEach((element) => {
    if (!element.dataset.part) mark(element, 'body', -1);
  }));
  root.querySelectorAll<HTMLElement>('section.docx > header p').forEach((element) => mark(element, 'header', -1));
  root.querySelectorAll<HTMLElement>('section.docx > footer p').forEach((element) => mark(element, 'footer', -1));
}

/** The text nodes under an element, in order. */
function textNodes(element: Node): Text[] {
  return Array.from(element.childNodes).flatMap((child) => (child.nodeType === Node.TEXT_NODE ? [child as Text] : textNodes(child)));
}

function chip(document: Document, text: string): HTMLElement {
  const span = document.createElement('span');
  span.className = CHIP;
  span.textContent = text;
  return span;
}

/**
 * Puts `insert` in place of the paragraph's text from `start` to `end`, as a highlighted chip, in the
 * run where the text began; later runs lose their part of it, as the server writes it.
 */
function spliceElement(element: HTMLElement, start: number, end: number, insert: string) {
  let offset = 0;
  let placed = false;
  textNodes(element).forEach((node) => {
    const value = node.nodeValue ?? '';
    const from = offset;
    const to = offset + value.length;
    offset = to;
    if (!value || to <= start || from >= end) return;
    const after = end < to ? value.slice(end - from) : '';
    if (placed) {
      node.nodeValue = after;
      return;
    }
    placed = true;
    node.nodeValue = start > from ? value.slice(0, start - from) : '';
    // A whole placeholder being replaced: the new one goes after the old chip, which empties.
    const anchor = node.parentElement?.closest(`.${CHIP}`) ?? node;
    const replacement = chip(element.ownerDocument, insert);
    anchor.after(replacement);
    if (after) replacement.after(element.ownerDocument.createTextNode(after));
  });
  element.querySelectorAll(`.${CHIP}`).forEach((old) => {
    if (!old.textContent) old.remove();
  });
}

/** Every match of `find` in the paragraph, replaced by `insert`, with the server's matching rule. */
function replaceInElement(element: HTMLElement, find: string, insert: string) {
  const text = textNodes(element).map((node) => node.nodeValue ?? '').join('');
  [...occurrences(text, find)].reverse().forEach((start) => spliceElement(element, start, start + find.length, insert));
}

/** Placeholders already typed in the file, highlighted as the inserted ones are. */
function highlightPlaceholders(element: HTMLElement) {
  const nodes = textNodes(element);
  const text = nodes.map((node) => node.nodeValue ?? '').join('');
  Array.from(text.matchAll(/\{[^{}]*\}/g)).reverse().forEach((match) => {
    if (!isCommandText(match[0].slice(1, -1))) return;
    const start = match.index ?? 0;
    let offset = 0;
    const startNode = textNodes(element).find((node) => {
      const from = offset;
      offset += (node.nodeValue ?? '').length;
      return start >= from && start < offset;
    });
    if (startNode?.parentElement?.closest(`.${CHIP}`)) return;
    spliceElement(element, start, start + match[0].length, match[0]);
  });
}

/**
 * Draws the pending changes onto the mapped page: repeated rows and paragraphs shaded and labelled,
 * then each paragraph's placeholders, a repeat's own first, as the server applies them.
 */
export function decorateDocument(root: HTMLElement, parts: WordLayoutPart[], changes: WordPlaceholderChanges, repeatLabel: (list: string) => string): void {
  const where = bodyParagraphs(parts);
  const label = (text: string) => {
    const element = root.ownerDocument.createElement('div');
    element.className = 'jt-repeat-label';
    element.textContent = text;
    return element;
  };

  changes.repeats.forEach((repeat) => {
    if (repeat.table !== undefined) {
      root.querySelectorAll<HTMLElement>(`tr[data-table="${repeat.table}"][data-row="${repeat.row}"]`).forEach((row, i) => {
        row.classList.add('jt-repeat-row');
        if (i === 0) row.querySelector('td, th')?.prepend(label(repeatLabel(repeat.list)));
      });
      return;
    }
    const covered = new Set(coveredBy(repeat, where));
    const inRepeat = Array.from(root.querySelectorAll<HTMLElement>('[data-part="body"][data-paragraph]')).filter((element) => covered.has(Number(element.dataset.paragraph)));
    inRepeat.forEach((element) => element.classList.add('jt-repeat'));
    inRepeat[0]?.before(label(repeatLabel(repeat.list)));
  });

  root.querySelectorAll<HTMLElement>('[data-part][data-paragraph]').forEach((element) => {
    const index = Number(element.dataset.paragraph);
    const r = element.dataset.part === 'body' && index >= 0 ? repeatOf(changes, where, 'body', index) : -1;
    const steps = [
      ...(r >= 0 ? changes.repeats[r].replacements.map((c) => ({ find: c.find, insert: placeholderText(c.placeholder, changes.repeats[r]) })) : []),
      ...changes.replacements.map((c) => ({ find: c.find, insert: placeholderText(c.placeholder) })),
    ];
    steps.forEach((step) => replaceInElement(element, step.find, step.insert));
    highlightPlaceholders(element);
  });
}
